import type {FC, ReactNode} from 'react';
import {createContext, useCallback, useContext, useState} from 'react';
import type {ContactNote} from 'api/v2';
import {NoteDetailsDialog} from './NoteDetailsDialog';

const ViewNoteDetailsContext = createContext<
  ((note: ContactNote) => void) | null
>(null);

export const useViewNoteDetails = () => {
  const viewDetails = useContext(ViewNoteDetailsContext);
  if (!viewDetails) {
    throw new Error(
      'useViewNoteDetails must be used inside <NoteDetailsProvider>',
    );
  }
  return viewDetails;
};

type NoteDetailsProviderProps = {
  children: ReactNode;
};

export const NoteDetailsProvider: FC<NoteDetailsProviderProps> = ({
  children,
}) => {
  // Kept after closing, so the dialog doesn't empty while it fades out
  const [note, setNote] = useState<ContactNote | null>(null);
  const [open, setOpen] = useState(false);
  const viewDetails = useCallback((selected: ContactNote) => {
    setNote(selected);
    setOpen(true);
  }, []);

  return (
    <ViewNoteDetailsContext.Provider value={viewDetails}>
      {children}
      {note && (
        <NoteDetailsDialog
          note={note}
          open={open}
          onClose={() => setOpen(false)}
        />
      )}
    </ViewNoteDetailsContext.Provider>
  );
};
